import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Sparkles, Activity } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyTherapeuticTestPreview - ÉCRAN 5 : Aperçu Réglage Personnalisé
 * Aperçu non interactif du test de personnalisation avec spectrogramme
 */
const JourneyTherapeuticTestPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [bars, setBars] = useState([]);
  const [calibrationValue, setCalibrationValue] = useState(50);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Générer les barres du spectrogramme
    const barsData = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      height: 30 + Math.random() * 50,
      delay: i * 0.03,
    }));
    setBars(barsData);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Animation du slider de calibration
    const interval = setInterval(() => {
      setCalibrationValue(prev => {
        const newVal = prev + 5;
        return newVal > 80 ? 30 : newVal;
      });
    }, 2000);
    
    return () => {
      clearInterval(interval);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('🎚️ JourneyTherapeuticTestPreview: Navigating to /journey-audio-quality');
    navigate('/journey-audio-quality');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(236, 72, 153, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(236, 72, 153, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <Settings className="w-6 h-6 text-pink-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'Hearen adapts sounds to your listening for optimal comfort.'
                  : 'Hearen adapte les sons à votre écoute pour un confort optimal.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Aperçu du Test de Calibration */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-8"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEnglish ? 'Personalized Setup' : 'Réglage Personnalisé'}
            </h2>
            <p className="text-pink-400 text-sm flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              {isEnglish ? 'Adapted to your listening' : 'Adapté à votre écoute'}
            </p>
          </div>

          {/* Spectrogramme animé (plus petit que thérapie sonore) */}
          <div 
            className="flex items-end justify-center gap-0.5 h-24 mb-6 px-2"
            style={{
              animation: 'spectro-gentle-pulse 3s ease-in-out infinite',
            }}
          >
            {bars.map((bar) => (
              <div
                key={bar.id}
                className="rounded-t-sm transition-all"
                style={{
                  width: '4px',
                  height: `${bar.height}%`,
                  background: 'linear-gradient(to top, rgba(236, 72, 153, 0.9) 0%, rgba(236, 72, 153, 0.6) 50%, rgba(236, 72, 153, 0.3) 100%)',
                  animation: `bar-calibration-flow ${1.5 + Math.random() * 1.5}s ease-in-out infinite`,
                  animationDelay: `${bar.delay}s`,
                  boxShadow: '0 0 4px rgba(236, 72, 153, 0.3)',
                }}
              />
            ))}
          </div>

          {/* Slider de calibration animé */}
          <div className="opacity-80 mb-6">
            <div className="flex items-center justify-between text-xs text-white/70 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-pink-400" />
                <span>{isEnglish ? 'Comfort Level' : 'Niveau de confort'}</span>
              </div>
              <span className="text-pink-400 font-semibold">{calibrationValue}%</span>
            </div>
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-2000"
                style={{
                  width: `${calibrationValue}%`,
                  background: 'linear-gradient(90deg, rgba(236, 72, 153, 0.6), rgba(236, 72, 153, 1))',
                  boxShadow: '0 0 12px rgba(236, 72, 153, 0.5)',
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-white/50 mt-2">
              <span>{isEnglish ? 'Soft' : 'Doux'}</span>
              <span>{isEnglish ? 'Intense' : 'Intense'}</span>
            </div>
          </div>

          {/* Indicateurs de profil */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="px-4 py-3 rounded-lg bg-pink-500/20 border border-pink-500/30">
              <div className="text-xs text-white/70 mb-1">
                {isEnglish ? 'Setting' : 'Réglage'}
              </div>
              <div className="text-white font-semibold text-sm">
                {isEnglish ? 'Balanced' : 'Équilibré'}
              </div>
            </div>
            <div className="px-4 py-3 rounded-lg bg-pink-500/20 border border-pink-500/30">
              <div className="text-xs text-white/70 mb-1">
                {isEnglish ? 'Adjustment' : 'Ajustement'}
              </div>
              <div className="text-white font-semibold text-sm">
                {isEnglish ? 'Light' : 'Léger'}
              </div>
            </div>
          </div>

          {/* Badge Preview */}
          <div className="text-center text-white/50 text-sm">
            {isEnglish ? 'Preview Mode' : 'Mode Aperçu'}
          </div>
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              boxShadow: '0 8px 30px rgba(236, 72, 153, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
          </button>
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes bar-calibration-flow {
          0%, 100% { 
            transform: scaleY(1); 
            opacity: 0.8; 
          }
          50% { 
            transform: scaleY(1.2); 
            opacity: 1; 
          }
        }
        
        @keyframes spectro-gentle-pulse {
          0%, 100% { 
            opacity: 0.95; 
            transform: scale(1); 
          }
          50% { 
            opacity: 1; 
            transform: scale(1.02); 
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyTherapeuticTestPreview;
